//src/Components/AIMito/useChats.ts
import { fetchPromptData } from './fetchPromptData';
import { getIntent } from './queryIntent'; // ← you were missing this
import { useState, useEffect } from 'react';
import { sendChatMessage } from 'app/services/chatService';

export const useChat = () => {
  const STORAGE_KEY_MESSAGES = 'aimito_chat_history';
  const STORAGE_KEY_SUGGESTIONS = 'aimito_suggestions';
  const STORAGE_KEY_HINT = 'aimito_show_hint';

  const DEFAULT_SUGGESTIONS = [
    'questionnaire?',
    'extension?',
    'accountant?',
    'upcoming dates?',
    'profit and loss?',
    'balance sheet?',
    'invoice?',
  ];

  const loadMessages = (): string[] => {
    const storedMessages = localStorage.getItem(STORAGE_KEY_MESSAGES);
    return storedMessages ? JSON.parse(storedMessages) : [];
  };

  const loadSuggestions = (): string[] => {
    const storedSuggestions = localStorage.getItem(STORAGE_KEY_SUGGESTIONS);
    return storedSuggestions
      ? JSON.parse(storedSuggestions)
      : DEFAULT_SUGGESTIONS;
  };

  const loadShowHint = (): boolean => {
    const storedHint = localStorage.getItem(STORAGE_KEY_HINT);
    return storedHint ? JSON.parse(storedHint) : true;
  };

  const [messages, setMessages] = useState<string[]>(loadMessages);
  const [suggestions, setSuggestions] = useState<string[]>(loadSuggestions);
  const [showHint, setShowHint] = useState<boolean>(loadShowHint);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_MESSAGES, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SUGGESTIONS, JSON.stringify(suggestions));
  }, [suggestions]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_HINT, JSON.stringify(showHint));
  }, [showHint]);

  useEffect(() => {
    const chatBox = document.getElementById('messages');
    if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
  }, [messages, loading]);

  // 🔹 Extract dates from AI response and format as suggestions
  const extractDatesAndCreateButtons = (message: string) => {
    const datePattern = /\d{4}-(\d{2}-\d{2}): \d{2}:\d{2} [AP]M/g;
    const matches = message.match(datePattern);

    if (matches) {
      const buttons = matches.map((match) => {
        const dateWithoutYear = match.replace(/\d{4}-/, ''); // Remove year
        return `schedule? ${dateWithoutYear}`;
      });

      setSuggestions(buttons);
    }
  };

  // 🔹 Format schedule date for API requests
  const formatScheduleDate = (dateStr: string) => {
    const datePattern = /(\d{2}-\d{2}): (\d{2}):(\d{2}) ([AP]M)/;
    const match = dateStr.match(datePattern);

    if (match) {
      const [_, monthDay, hours, minutes, period] = match;

      let hour = parseInt(hours, 10);
      if (period === 'PM' && hour < 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;

      return `2025-${monthDay}T${String(hour).padStart(
        2,
        '0',
      )}:${minutes}:00-0400`;
    }
    return dateStr;
  };

  const sendMessage = async (question: string) => {
    if (!question.trim()) return;
    setMessages((prev) => [...prev, `You: ${question}`]);

    if (getIntent(question)) {
      setLoading(true);
      const result = await fetchPromptData(question);
      setLoading(false);

      if (result.status === 'error') {
        setMessages((m) => [...m, `AI Mito: ${result.error}`]);
        return;
      }
      if (result.status === 'success') {
        setMessages((m) => [...m, `AI Mito:\n${result.payload}`]);
        return; // ✔ handled, don’t fall through to LLM
      }
    }
    /* ----------------- LLM fallback (unchanged) ------------------- */

    setLoading(true);
    setError('');
    setShowHint(false);

    try {
      if (question.toLowerCase().includes('ticket')) {
        document.getElementById('client-support-btn')?.click();
        setMessages((prev) => [
          ...prev,
          'AI Mito: Opening the ticket submission form...',
        ]);
        return;
      }

      const response = await sendChatMessage(question, 120_000);
      setMessages((prev) => [...prev, `AI Mito: ${response}`]);
      setLoading(false); // 👈 add this line after handling the response
      // 🔹 Handle Availability and Scheduling
      if (response.includes('availability')) {
        extractDatesAndCreateButtons(response);
      } else {
        setSuggestions(DEFAULT_SUGGESTIONS);
      }
    } catch (err) {
      resetChat(); // 🧹 Clear the chat nicely
      setMessages([
        "AI Mito: We're experiencing high server load. Please wait a few seconds and type your prompt again.",
      ]);
      setLoading(false); // 👈 add this line after handling the response
    }
  };

  // 🔹 Handle user clicking on suggested responses
  const handleSuggestionClick = (suggestion: string) => {
    let messageToSend = suggestion;

    if (suggestion.startsWith('schedule?')) {
      const dateStr = suggestion.replace('schedule? ', '');
      messageToSend = `schedule?${formatScheduleDate(dateStr)}?`;
    }

    sendMessage(messageToSend);
  };

  // 🔹 Reset function
  const resetChat = () => {
    localStorage.removeItem(STORAGE_KEY_MESSAGES);
    localStorage.removeItem(STORAGE_KEY_SUGGESTIONS);
    localStorage.removeItem(STORAGE_KEY_HINT);

    setMessages([]);
    setSuggestions(DEFAULT_SUGGESTIONS);
    setShowHint(true);
    setError('');
  };

  return {
    messages,
    suggestions,
    loading,
    error,
    showHint,
    sendMessage,
    handleSuggestionClick,
    resetChat,
  };
};
